import { useGetTransitionalCareRequests } from "apollo/hooks/queries";
import { composeProgress } from "apollo/utils";
import { MAX_REQUESTS } from "core/consts";
import { getErrorMessage } from "core/model/utils/errors";
import { TransitionalCareRequest } from "core/types";
import CheckboxInputField from "ds_legacy/components/CheckboxInputField";
import RSButton from "ds_legacy/components/RSButton";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { margin, padding, sizing } from "ds_legacy/materials/metrics";
import { GAP, SectionRow } from "dsl/ecosystems/PatientForms/components";
import { FormElement, FormElementProps } from "react-forms-state";
import { useTranslations } from "translations";
import { REQUEST_LIST_ELEMENT_NAME } from "..";
import {
  RequestListContextState,
  RequestListProvider,
  useRequestListContext,
} from "../RequestListContext";
import { ACTION_TYPES } from "../RequestListContext/reducer";
import { EditCard } from "./EditCard";
import { TransitionalCareRequestCard } from "./RequestCard";

export const getSectionIdLabel = (id: number): string =>
  id < 10 ? `0${id}` : id.toString();

const HeaderSection = ({ auctionId }: { auctionId: number }) => {
  const { dispatch, state } = useRequestListContext();
  const translations = useTranslations();

  const [queryProgress, getRequests] = useGetTransitionalCareRequests({
    auctionId,
  });

  return (
    <SectionRow justify="space-between">
      <CheckboxInputField
        elementName="internet_portal_used"
        flatModel
        label={
          translations.patientForms.transitionalCareForm.sectionE
            .internetbasedPortals
        }
        labelStyleOverride={{ margin: margin(0) }}
      />
      <RSButton
        color="primary"
        id="import_requests"
        loading={composeProgress([queryProgress], true)}
        style={{ margin: "0" }}
        variant="contained"
        onClick={async () => {
          try {
            const requests = await getRequests();
            dispatch({
              payload: {
                requests: requests ?? [],
              },
              type: ACTION_TYPES.IMPORT_REQUESTS,
            });
          } catch (err) {
            console.error(
              `error importing transitional care requests - ${getErrorMessage(
                err,
              )}`,
            );
            return null;
          }
        }}
      >
        {state.request_list.some((r) => !r.manual)
          ? translations.patientForms.transitionalCareForm.sectionE
              .refreshButton
          : translations.patientForms.transitionalCareForm.sectionE
              .importButton}
      </RSButton>
    </SectionRow>
  );
};

export const showAddRequestsButton = (
  state: RequestListContextState,
): boolean => {
  if (state.adding) {
    return false;
  }
  if (state.request_list.length >= MAX_REQUESTS) {
    return false;
  }
  return true;
};

const AddRequestButton = () => {
  const { dispatch, state } = useRequestListContext();
  const translations = useTranslations();

  if (!showAddRequestsButton(state)) {
    return null;
  }

  return (
    <SectionRow justify="space-between">
      <RSButton
        color="default"
        id="add_request"
        inverted
        loading="na"
        style={{ margin: "0" }}
        variant="contained"
        onClick={() => {
          dispatch({ type: ACTION_TYPES.OPEN_ADD });
        }}
      >
        {
          translations.patientForms.transitionalCareForm.sectionE
            .addMoreProviders
        }
      </RSButton>
    </SectionRow>
  );
};

const RequestCards = () => {
  const { state } = useRequestListContext();

  if (!state.request_list.length) return null;

  return (
    <VerticalLayout padding={padding(GAP, 0, 0)} rowGap={sizing(1)}>
      {state.request_list.map((request, i) => {
        const displayId = i + 1;

        if (state.editing?.id === request.id) {
          return (
            <EditCard
              key={request.id}
              displayId={displayId}
              isEdit
              request={state.editing}
            />
          );
        }

        return (
          <TransitionalCareRequestCard key={request.id} id={displayId} request={request} />
        );
      })}
    </VerticalLayout>
  );
};

const RequestListForm = ({ auctionId }: { auctionId: number }) => {
  const { state } = useRequestListContext();

  return (
    <>
      <HeaderSection auctionId={auctionId} />
      <RequestCards />
      <EditCard
        displayId={state.request_list.length + 1}
        request={state.adding}
      />
      <AddRequestButton />
    </>
  );
};

type RequestListProps = {
  auctionId: number;
  onChange: (
    value: TransitionalCareRequest[],
    statePath: typeof REQUEST_LIST_ELEMENT_NAME,
    validation: any,
  ) => void;
  value: TransitionalCareRequest[] | null;
} & FormElementProps;

const RequestList = ({ auctionId, onChange, value }: RequestListProps) => {
  const handleSubmit = (requests: TransitionalCareRequest[]) =>
    onChange(requests, REQUEST_LIST_ELEMENT_NAME, null);

  return (
    <RequestListProvider formValue={value || []} handleSubmit={handleSubmit}>
      <RequestListForm auctionId={auctionId} />
    </RequestListProvider>
  );
};

export const RequestListElement = FormElement()<RequestListProps>(RequestList);
